import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdDelete, MdOutlineModeEdit, MdRemoveRedEye } from "react-icons/md";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PopUpForm from "../../components/PopUpForm";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import { useApi } from "../../services/api";
import { LocalizedText, Subsidiary } from "../../services/generated/client";
import {
  createSubsidiary,
  getAllSubsidiaries,
  removeSubsidiary,
  updateSubsidiary,
} from "../../services/subsidiary/subsidiaryServices";
import styles from "./styles.module.css";

const Subsidiaries = () => {
  const { withErrorHandling } = useApi();
  const [subsidiaries, setSubsidiaries] = useState<Subsidiary[]>([]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState<Subsidiary>({
    _id: {},
    name: localizedTextDefaultValue,
    cardImageUrl: "",
    cardText: localizedTextDefaultValue,
    website: "",
    header1: localizedTextDefaultValue,
    header2: localizedTextDefaultValue,
    paragraph1: localizedTextDefaultValue,
    paragraph2: localizedTextDefaultValue,
    imageUrls: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState(false);
  const [languageEditing, setLanguageEditing] =
    useState<keyof LocalizedText>("ka");

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    withErrorHandling(async () => {
      const response = await getAllSubsidiaries();
      if (response) setSubsidiaries(response);
    });
  }, []);

  const handleEdit = (subsidiary: Subsidiary) => {
    setSelectedSubsidiary(subsidiary);
    setIsAdding(false);
    setView(false);
    setIsModalOpen(true);
  };

  const handleView = (subsidiary: Subsidiary) => {
    setSelectedSubsidiary(subsidiary);
    setIsAdding(false);
    setView(true);
    setIsModalOpen(true);
  };

  const handleDelete = useCallback(
    async (_id: string, name: string) => {
      if (window.confirm(`Are you sure you want to delete ${name}`)) {
        await withErrorHandling(async () => {
          await removeSubsidiary(_id);
          setSubsidiaries((prev) =>
            prev.filter((subsidiary) => subsidiary._id.toString() !== _id)
          );
        });
      }
    },
    [withErrorHandling]
  );

  const handleUpdateSubsidiary = async (e: FormEvent) => {
    e.preventDefault();
    const {
      _id,
      name,
      cardImageUrl,
      cardText,
      website,
      header1,
      header2,
      paragraph1,
      paragraph2,
      imageUrls,
    } = selectedSubsidiary;

    const cleanedImageUrls = imageUrls.filter((url) => url.trim() !== "");

    await withErrorHandling(async () => {
      if (isAdding) {
        const newSubsidiary = await createSubsidiary({
          name,
          cardImageUrl,
          cardText,
          website,
          header1,
          header2,
          paragraph1,
          paragraph2,
          imageUrls: cleanedImageUrls,
        });
        setSubsidiaries((prev) => [...prev, newSubsidiary]);
      } else {
        await updateSubsidiary(_id.toString(), {
          name,
          cardImageUrl,
          cardText,
          website,
          header1,
          header2,
          paragraph1,
          paragraph2,
          imageUrls: cleanedImageUrls,
        });
        setSubsidiaries((prev) =>
          prev.map((subsidiary) =>
            subsidiary._id === _id ? selectedSubsidiary : subsidiary
          )
        );
      }
      setIsModalOpen(false);
    });
  };

  const handleAddSubsidiary = () => {
    setSelectedSubsidiary({
      _id: {},
      name: localizedTextDefaultValue,
      cardImageUrl: "",
      cardText: localizedTextDefaultValue,
      website: "",
      header1: localizedTextDefaultValue,
      header2: localizedTextDefaultValue,
      paragraph1: localizedTextDefaultValue,
      paragraph2: localizedTextDefaultValue,
      imageUrls: [],
    });
    setIsAdding(true);
    setIsModalOpen(true);
  };

  const columns = useMemo<MRT_ColumnDef<Subsidiary>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => row.original.name?.[languageEditing] || "",
      },
      {
        accessorKey: "header1",
        header: "Header 1",
        Cell: ({ row }) => row.original.header1?.[languageEditing] || "",
      },
      {
        accessorKey: "header2",
        header: "Header 2",
        Cell: ({ row }) => row.original.header2?.[languageEditing] || "",
      },
      {
        header: "Actions",
        Cell: ({ row }: { row: { original: Subsidiary } }) => {
          return (
            <div className={styles.actionBtns}>
              <button
                onClick={() => handleView(row.original)}
                className={styles.tableBtn}
              >
                <MdRemoveRedEye />
              </button>
              <button
                onClick={() => handleEdit(row.original)}
                className={styles.tableBtn}
              >
                <MdOutlineModeEdit />
              </button>
              <button
                onClick={() =>
                  handleDelete(
                    row.original._id.toString(),
                    row.original.name.en
                  )
                }
                className={`${styles.tableBtn} ${styles.delete}`}
              >
                <MdDelete />
              </button>
            </div>
          );
        },
      },
    ],
    [handleDelete, languageEditing]
  );

  const handleMultiLanguageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id as keyof Subsidiary;
    const value = e.target.value;

    setSelectedSubsidiary((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] as LocalizedText),
        [languageEditing]: value,
      },
    }));
  };

  return (
    <div className={styles.mainContent}>
      <h2>Manage Subsidiaries</h2>
      <div className={styles.tableWrapper}>
        <MaterialReactTable
          columns={columns}
          data={Array.isArray(subsidiaries) ? subsidiaries : []}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", gap: "20px" }}>
              <button className={styles.addBtn} onClick={handleAddSubsidiary}>
                + Add Subsidiary
              </button>
              <div>
                <LanguageSwitcher
                  languageEditing={languageEditing}
                  setLanguageEditing={setLanguageEditing}
                  inForm={false}
                />
              </div>
            </div>
          )}
          muiTableContainerProps={{
            sx: {
              flex: 1,
              overflowY: "hidden",
            },
          }}
          muiTablePaperProps={{
            sx: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableBodyProps={{
            sx: {
              overflowY: "auto",
              height: "100%",
            },
          }}
          muiTableProps={{
            sx: {
              maxHeight: "100%",
              gridTemplateRows: "auto 1fr",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              overflow: "auto",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          layoutMode="grid"
        />
      </div>

      {isModalOpen && selectedSubsidiary && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} ref={modalRef}>
            <button
              className={styles.closeButton}
              onClick={() => setIsModalOpen(false)}
            >
              X
            </button>
            <LanguageSwitcher
              languageEditing={languageEditing}
              setLanguageEditing={setLanguageEditing}
              inForm={true}
            />
            <PopUpForm
              title={"Subsidiary"}
              submitLabel={isAdding ? "Create" : "Update"}
              onSubmit={handleUpdateSubsidiary}
              isView={view}
              languageEditing={languageEditing}
              isAdding={isAdding}
              fields={[
                {
                  id: "website",
                  label: "Website",
                  type: "text",
                  value: selectedSubsidiary?.website || "",
                  onChange: (e) =>
                    setSelectedSubsidiary((prev) => ({
                      ...prev,
                      website: e.target.value,
                    })),
                },
                {
                  id: "cardImageUrl",
                  label: "Card Image",
                  type: "imageUrl",
                  value: selectedSubsidiary?.cardImageUrl || "",
                  onImageChange: (_, url) =>
                    setSelectedSubsidiary((prev) => ({
                      ...prev,
                      cardImageUrl: url,
                    })),
                },
                {
                  id: "name",
                  label: `Name ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedSubsidiary?.name?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "cardText",
                  label: `Card Text ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedSubsidiary?.cardText?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "header1",
                  label: `Header 1 ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedSubsidiary?.header1?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "header2",
                  label: `Header 2 ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedSubsidiary?.header2?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "paragraph1",
                  label: `Paragraph 1  ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedSubsidiary?.paragraph1?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },

                {
                  id: "paragraph2",
                  label: `Paragraph 2  ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedSubsidiary?.paragraph2?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "imageUrls",
                  label: "Image URL",
                  type: "imageUrls",
                  values: selectedSubsidiary?.imageUrls || [],
                  onImageChange: (index, value) => {
                    setSelectedSubsidiary((prev) => {
                      const updatedImageUrls = [...prev.imageUrls];
                      updatedImageUrls[index] = value;
                      return { ...prev, imageUrls: updatedImageUrls };
                    });
                  },
                  onAddImage: () => {
                    setSelectedSubsidiary((prev) => ({
                      ...prev,
                      imageUrls: [...prev.imageUrls, ""],
                    }));
                  },
                  onRemoveImage: (index) => {
                    setSelectedSubsidiary((prev) => {
                      const updatedImageUrls = [...prev.imageUrls];
                      updatedImageUrls.splice(index, 1);
                      return { ...prev, imageUrls: updatedImageUrls };
                    });
                  },
                },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Subsidiaries;
